import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { ISiteMetadata } from '~src/siteManagement/serverApi/types/ISiteMetadata';

import { SiteSettingsForSiteAdminProps } from '../../site-settings/ReactSiteSettingsForSiteAdmin';

export type AgentId = 'pulse' | 'webAuthoring' | 'studio' | 'prep' | 'catalog' |  'pulseDiscover' | 'pulseEmbeddings' | 'dashboardInsights' | 'pulseDefinitionAuthoringAssistant' | 'tableauKnowledge' | 'tableauAiSearch' | 'agenticSubscriptions';

// Nested sub-object of the Formik `settings` that an agent's settings can live under.
// Extend this union as new namespaces are introduced.
export type SettingsNamespace = 'siteSettingsV2';

export const SITE_SETTINGS_V2_NAMESPACE: SettingsNamespace = 'siteSettingsV2';

// Top-level (legacy) siteAdminSettings keys. These drive the Pick<ISiteAdminSettings>
// props type below, so every member MUST be a real key of ISiteAdminSettings.
export type SettingsForAgents_enabled =    'generativeAiEnabled' |
                                    'generativeAiWebAuthoring' |
                                    'generativeAiWebAuthoringOnPremEnabled' |
                                    'generativeAiPrep' |
                                    'generativeAiDataCatalog' |
                                    'generativeAiPulseDiscoverEnabled' |
                                    'dashboardNarrativeEnabled' |
                                    'dashboardInsightsEnabled' |
                                    'generativeAiPulseExternalEmbeddingsEnabled';

export type SettingsForAgents_groupId =    'generativeAiGroupId' |
                                    'generativeAiWebAuthoringGroupId' |
                                    'generativeAiPrepGroupId' |
                                    'generativeAiDataCatalogGroupId'|
                                    'generativeAiPulseDiscoverGroupId' |
                                    'dashboardInsightsGroupId' |
                                    'generativeAiPulseExternalEmbeddingsGroupId';

// siteSettingsV2-nested keys. Kept separate from the legacy unions above because they
// are not keys of ISiteAdminSettings (they live in the untyped siteSettingsV2 bag), so
// folding them in would fail the Pick<> keyof constraint (TS2344).
export type V2SettingsForAgents_enabled = 'generativeAiPulseDefinitionAuthoringAssistantEnabled' | 'tableauKnowledgeActivated' | 'tableauAiSearchEnabled' | 'tableauStudioAgentActivated' | 'dashboardQAEnabled' | 'generativeAiAgenticSubscriptionsEnabled';

export type V2SettingsForAgents_groupId = 'generativeAiPulseDefinitionAuthoringAssistantGroupId' | 'tableauAiSearchGroupId' | 'tableauStudioAgentGroupId' | 'generativeAiAgenticSubscriptionsGroupLuid';

// Online site-metadata keys that carry a license-synced per-feature entitlement, not an admin
// toggle. Read from the site metadata, so this is constrained to real ISiteMetadata keys.
export type AgentEntitlementMetadataKey = Extract<keyof ISiteMetadata, 'tableauStudioAgentEnabled'>;

// The two Agent fields that hold a settings key. Used to select which perm
// getAgentFieldPath() resolves, instead of passing bare 'enabledSiteSetting'/'groupIdSiteSetting'
// string literals at the call sites.
export enum AgentPerm {
  Enabled = 'enabledSiteSetting',
  GroupId = 'groupIdSiteSetting',
}

export type SettingsForAgents = SettingsForAgents_enabled | SettingsForAgents_groupId;

export interface Agent {
  enabled: boolean;
  groupId: string | null;
  id: AgentId;
  // Getter for the agent's localized display name. A getter (not a plain string) so Messages
  // are read lazily at render time, not at module-init when the i18n bundle may not be ready.
  displayName: () => string;
  // Getter for the agent's localized description. Joined onto displayName via
  // messageWithColonSeparator in getAgentLabel(). Required for every agent.
  description: () => string;
  enabledSiteSetting: SettingsForAgents_enabled | V2SettingsForAgents_enabled;
  // Nullable: agents with no group-based access support (e.g. tableauKnowledge) set this to null.
  groupIdSiteSetting: SettingsForAgents_groupId | V2SettingsForAgents_groupId | null;
  // Optional per-feature Cloud entitlement key, read from the license-synced online site metadata
  // (distinct from the admin toggle in enabledSiteSetting). When set, the row is only available on
  // Cloud (isOnline) if the metadata value is truthy; an unentitled site shows "Free Trial Not
  // Available" and the Actions menu is disabled, mirroring the Tableau+ (isTableauPlus) treatment but
  // scoped to one feature. Ignored off Cloud, where on-prem licensing is gated via
  // GenerativeAIOnPremSection.
  entitlementMetadataKey?: AgentEntitlementMetadataKey;
  // Omitted for legacy settings (flattened into `settings.*`); set to a namespace
  // for settings nested under `settings.<namespace>.*`. See getAgentFieldPath().
  settingsNamespace?: SettingsNamespace;
  // Per-perm namespace overrides. When set, they take precedence over settingsNamespace for
  // that single perm — this lets a Server agent bind its enable key to a nested namespace
  // (siteSettingsV2) while its groupId key stays top-level. See getAgentFieldPath().
  enabledNamespace?: SettingsNamespace;
  groupIdNamespace?: SettingsNamespace;
  // Requires tenant to register a Salesforce org
  requiresRegistration: boolean;
  // If a tenant is not configured, it utilizes a fallback org
  hasFallbackRegistration?: boolean;
  flag?: keyof typeof FeatureFlags;
  availableInTrial: boolean;
  availableInTableauAITrial: boolean;
  betaFlag?: keyof typeof FeatureFlags;
  readyForGroupsFlag?:  keyof typeof FeatureFlags;
  dashboardQAFlag?: keyof typeof FeatureFlags;
}


export type AgentPermissionsSectionProps_enabled = SiteSettingsForSiteAdminProps<SettingsForAgents_enabled>;
export type AgentPermissionsSectionProps_groupId = SiteSettingsForSiteAdminProps<SettingsForAgents_groupId>;
export type AgentPermissionsSectionProps = SiteSettingsForSiteAdminProps<SettingsForAgents>;
